import { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../api/axiosInstance';

const baseUrl = process.env.REACT_APP_API_URL;

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  languagePreference: string;
}

interface UseUsersParams {
  search?: string;
  page?: number;
  limit?: number;
}

const useUsers = (params: UseUsersParams = {}) => {
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { search = '', page = 1, limit = 10 } = params;

  // Fetch all users once on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`${baseUrl}/admin/users`);
        setAllUsers(response.data);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Error fetching users.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Client-side filtering and pagination
  const { users, totalCount, totalPages } = useMemo(() => {
    // Filter users based on search term
    let filteredUsers = allUsers;

    if (search.trim()) {
      filteredUsers = allUsers.filter(
        (user: User) =>
          user.name.toLowerCase().includes(search.toLowerCase()) ||
          user.email.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Calculate pagination
    const totalCount = filteredUsers.length;
    const totalPages = Math.ceil(totalCount / limit);

    // Get users for current page
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    return {
      users: paginatedUsers,
      totalCount,
      totalPages,
    };
  }, [allUsers, search, page, limit]);

  return {
    users,
    totalCount,
    totalPages,
    currentPage: page,
    loading,
    error,
  };
};

export default useUsers;
